// Calificación de «Comportamiento del conductor» (en Dumax Pro/Mapon: Driver
// behavior). Pesos y umbrales los pone cada compañía (GET/PUT
// /companies/driving-score-settings); aquí, los mismos valores por omisión
// que el API para cuando todavía no está desplegado o no hay nada guardado.
//
// De dónde sale cada evento brusco (medido el 23-oct-2026, 400 equipos-día):
// - Ruptela (Eco5 Lite, Trace5): contadores eco en CADA posición
//   (eco_extreme_and_harsh_brake_counter en 55 equipos, eco_harsh_acceleration
//   _counter en 52, eco_cornering_counter en 127). Valen 0 y suben a 1 en el
//   registro del evento (io_caused 135); hay equipos con el valor FIJO en 1,
//   así que sólo cuenta el paso de 0 a > 0.
// - Teltonika: green_driving_type 1 aceleración, 2 frenado, 3 giro.
// - El resto: eventos v5 34 (aceleración) y 35 (frenado). El 25 («turn») NO:
//   es el registro por cambio de rumbo, no un giro brusco (1 042 en 13 equipos
//   en un día).
import { metrosEntre } from "../../../components/map/movimientoSuave";

export interface ScoreSettings {
  ecoSpeedKmh: number;
  speedLimitKmh: number;
  idleMinutes: number;
  eventsPer100kmForZero: number;
  weights: Record<ParamKey, number>;
}

export type ParamKey = "eco" | "idle" | "harshBrake" | "harshAccel" | "harshTurn" | "speeding";

export const PARAM_KEYS: ParamKey[] = ["eco", "idle", "harshBrake", "harshAccel", "harshTurn", "speeding"];

export const DEFAULT_SETTINGS: ScoreSettings = {
  ecoSpeedKmh: 85,
  speedLimitKmh: 90,
  idleMinutes: 2,
  eventsPer100kmForZero: 10,
  weights: { eco: 5, idle: 5, harshBrake: 5, harshAccel: 5, harshTurn: 5, speeding: 5 },
};

export interface ScorePoint {
  ts: number;
  lat: number;
  lng: number;
  speed?: number | null;
  ignition?: boolean | null;
  event_code?: number | null;
  params?: Record<string, unknown> | null;
}

export interface DrivingEvent {
  kind: "harshBrake" | "harshAccel" | "harshTurn" | "speeding" | "idle";
  ts: number;
  lat: number;
  lng: number;
  durationMs?: number;
  speed?: number;
}

export interface ParamResult {
  score: number | null; // null = el equipo no manda ese dato
  value: number; // % (eco, ralentí, exceso) o cantidad (bruscos)
}

export interface DrivingScore {
  overall: number | null;
  params: Record<ParamKey, ParamResult>;
  distanceKm: number;
  drivingMs: number;
  engineOnMs: number;
  idleMs: number;
  avgSpeed: number | null;
  events: DrivingEvent[];
}

// Más de 5 min entre dos puntos es un hueco de reporte: no se reparte como
// manejo ni como ralentí (inflaría los dos).
const MAX_STEP_MS = 5 * 60 * 1000;
const MOVING_KMH = 5;
const IDLE_KMH = 3;

const num = (v: unknown): number | null => {
  const n = typeof v === "string" ? parseFloat(v) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) ? n : null;
};

const clamp = (v: number) => Math.max(0, Math.min(100, v));

const COUNTERS: Record<"harshBrake" | "harshAccel" | "harshTurn", string> = {
  harshBrake: "eco_extreme_and_harsh_brake_counter",
  harshAccel: "eco_harsh_acceleration_counter",
  harshTurn: "eco_cornering_counter",
};
const GREEN_DRIVING: Record<number, "harshAccel" | "harshBrake" | "harshTurn"> = { 1: "harshAccel", 2: "harshBrake", 3: "harshTurn" };
const EVENT_CODES: Record<number, "harshAccel" | "harshBrake"> = { 34: "harshAccel", 35: "harshBrake" };

export const gradeOf = (score: number | null): string => {
  if (score == null) return "—";
  // A 90-100 y G 0-14.99 son los de Mapon; los cortes intermedios, cada 15.
  if (score >= 90) return "A";
  if (score >= 75) return "B";
  if (score >= 60) return "C";
  if (score >= 45) return "D";
  if (score >= 30) return "E";
  if (score >= 15) return "F";
  return "G";
};

export const scoreDriving = (input: ScorePoint[], s: ScoreSettings = DEFAULT_SETTINGS): DrivingScore => {
  const pts = [...input].sort((a, b) => a.ts - b.ts);
  const events: DrivingEvent[] = [];
  const supports = { harshBrake: false, harshAccel: false, harshTurn: false };
  const prevCounter: Record<string, number> = {};

  let drivingMs = 0;
  let ecoMs = 0;
  let overMs = 0;
  let engineOnMs = 0;
  let idleMs = 0;
  let distanceKm = 0;
  let overspeeding = false;
  let idleStart: ScorePoint | null = null;
  let idleRun = 0;

  const closeIdle = () => {
    // «Más de» idleMinutes, como Mapon (más de 2 min): justo 2 min no cuenta.
    if (idleStart && idleRun > s.idleMinutes * 60000) {
      idleMs += idleRun;
      events.push({ kind: "idle", ts: idleStart.ts, lat: idleStart.lat, lng: idleStart.lng, durationMs: idleRun });
    }
    idleStart = null;
    idleRun = 0;
  };

  pts.forEach((p, i) => {
    const params = p.params ?? {};
    const hit = new Set<DrivingEvent["kind"]>();

    for (const [kind, key] of Object.entries(COUNTERS) as [keyof typeof COUNTERS, string][]) {
      const v = num(params[key]);
      if (v == null) continue;
      supports[kind] = true;
      if (v > 0 && prevCounter[key] === 0) hit.add(kind);
      prevCounter[key] = v;
    }
    const gd = num(params.green_driving_type);
    if (gd != null) {
      supports.harshAccel = supports.harshBrake = supports.harshTurn = true;
      if (GREEN_DRIVING[gd]) hit.add(GREEN_DRIVING[gd]);
    }
    const code = num(p.event_code);
    if (code != null && EVENT_CODES[code]) {
      supports[EVENT_CODES[code]] = true;
      hit.add(EVENT_CODES[code]);
    }
    hit.forEach((kind) => events.push({ kind, ts: p.ts, lat: p.lat, lng: p.lng, speed: num(p.speed) ?? undefined }));

    if (i === 0) return;
    const a = pts[i - 1];
    const dt = p.ts - a.ts;
    if (dt <= 0 || dt > MAX_STEP_MS) {
      closeIdle();
      overspeeding = false;
      return;
    }
    const speed = num(a.speed) ?? 0;
    if (a.ignition === true) engineOnMs += dt;
    if (speed > MOVING_KMH) {
      drivingMs += dt;
      if (speed <= s.ecoSpeedKmh) ecoMs += dt;
      if (speed > s.speedLimitKmh) {
        overMs += dt;
        if (!overspeeding) events.push({ kind: "speeding", ts: a.ts, lat: a.lat, lng: a.lng, speed });
        overspeeding = true;
      } else overspeeding = false;
      const km = metrosEntre(a, p) / 1000;
      // Un salto imposible (> 200 km/h) no suma kilómetros.
      if (km / (dt / 3600000) <= 200) distanceKm += km;
      closeIdle();
    } else if (a.ignition === true && speed <= IDLE_KMH) {
      if (!idleStart) idleStart = a;
      idleRun += dt;
    } else {
      closeIdle();
    }
  });
  closeIdle();

  const pct = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0);
  const per100 = (n: number) => (distanceKm > 0 ? (n / distanceKm) * 100 : 0);
  const count = (k: DrivingEvent["kind"]) => events.filter((e) => e.kind === k).length;
  const harsh = (k: "harshBrake" | "harshAccel" | "harshTurn"): ParamResult => ({
    score: supports[k] && drivingMs > 0 ? clamp(100 - per100(count(k)) * (100 / s.eventsPer100kmForZero)) : null,
    value: count(k),
  });

  const ecoPct = pct(ecoMs, drivingMs);
  const idlePct = pct(idleMs, engineOnMs);
  const overPct = pct(overMs, drivingMs);
  const params: Record<ParamKey, ParamResult> = {
    eco: { score: drivingMs > 0 ? ecoPct : null, value: ecoPct },
    // Mapon pone F con ~30 % de ralentí y G con 100 %: con ×2, 50 % ya es 0.
    idle: { score: engineOnMs > 0 ? clamp(100 - idlePct * 2) : null, value: idlePct },
    harshBrake: harsh("harshBrake"),
    harshAccel: harsh("harshAccel"),
    harshTurn: harsh("harshTurn"),
    // 20 % del tiempo de manejo arriba del límite deja el parámetro en 0.
    speeding: { score: drivingMs > 0 ? clamp(100 - overPct * 5) : null, value: overPct },
  };

  let wSum = 0;
  let acc = 0;
  for (const k of PARAM_KEYS) {
    const w = s.weights[k] ?? 0;
    const sc = params[k].score;
    if (w > 0 && sc != null) {
      wSum += w;
      acc += w * sc;
    }
  }

  return {
    overall: wSum > 0 ? acc / wSum : null,
    params,
    distanceKm,
    drivingMs,
    engineOnMs,
    idleMs,
    avgSpeed: drivingMs > 0 ? distanceKm / (drivingMs / 3600000) : null,
    events: events.sort((x, y) => x.ts - y.ts),
  };
};
